import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Card } from '@/components/ui/card'
import { TopicLandingCard } from '../components/TopicLandingCard'
import { TopicLandingPagination } from '../components/TopicLandingPagination'
import { TopicLandingPageSkeleton } from '../components/TopicLandingPageSkeleton'
import { TopicLandingToolbar } from '../components/TopicLandingToolbar'
import { useTopicLandingContent } from '../hooks/useTopicLandingContent'
import {
  ARTICLES_PER_PAGE,
  formatTopicTitle,
  getTopicLandingItemHref,
  getTopicLandingItemTitle,
} from '../utils/guide.utils'
import { GuidePageHeader } from '../components/GuidePageHeader'

const TopicLandingPage = () => {
  const { mainTopic } = useParams()
  const { items, mainTopicContent, isLoading } = useTopicLandingContent(mainTopic)

  const [currentPage, setCurrentPage] = useState(1)
  const [search, setSearch] = useState('')

  useEffect(() => {
    setSearch('')
    setCurrentPage(1)
  }, [mainTopic])

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase()

    if (!query) return items

    return items.filter((item) => {
      return [item.title, item.description, item.subTabTitle]
        .filter(Boolean)
        .some((value) => value?.toLowerCase().includes(query))
    })
  }, [items, search])

  const totalPages = useMemo(
    () => Math.ceil(filteredItems.length / ARTICLES_PER_PAGE),
    [filteredItems.length],
  )
  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * ARTICLES_PER_PAGE
    return filteredItems.slice(startIndex, startIndex + ARTICLES_PER_PAGE)
  }, [filteredItems, currentPage])

  const goToPage = useCallback(
    (page: number) => {
      setCurrentPage(Math.min(Math.max(page, 1), Math.max(totalPages, 1)))
    },
    [totalPages],
  )

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value)
    setCurrentPage(1)
  }, [])

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
      <GuidePageHeader
        header={mainTopicContent?.title ?? formatTopicTitle(mainTopic)}
        subheader={mainTopicContent?.description ?? ''}
      />

      <TopicLandingToolbar
        count={filteredItems.length}
        search={search}
        mainTopic={mainTopic}
        onSearchChange={handleSearchChange}
      />

      {isLoading ? (
        <TopicLandingPageSkeleton />
      ) : (
        <>
          <Card className="overflow-hidden p-0">
            <ul role="list" className="divide-y divide-border">
              {paginatedItems.length === 0 ? (
                <li className="px-4 py-8 text-center text-sm text-muted-foreground sm:px-6">
                  No items found.
                </li>
              ) : (
                paginatedItems.map((item) => {
                  const href = getTopicLandingItemHref(item)

                  return (
                    <TopicLandingCard
                      key={`${item.contentType}-${item.documentId ?? item.id ?? href}`}
                      title={getTopicLandingItemTitle(item)}
                      href={href}
                      subTabTitle={item.subTabTitle}
                      contentType={item.contentType}
                    />
                  )
                })
              )}
            </ul>
          </Card>

          <TopicLandingPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={goToPage}
          />
        </>
      )}
    </div>
  )
}

export default TopicLandingPage
